import {
  Avatar,
  Box,
  Button,
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FilterableSearchBar from 'components/FilterableSearchBar';
import FlexBox from 'components/flexbox/FlexBox';
import FlexRowAlign from 'components/flexbox/FlexRowAlign';
import ManualDataInputModal from 'components/modal/report-creator/ManualDataInputModal';
import ReversePagination from 'components/ReversePagination';
import { H5, Small, Tiny } from 'components/Typography';
import { ArrowDownToLine, Check, CircleHelp, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import SuccessModal2 from '../../components/modal/alerts/SuccessModal2';
import InfoModal from '../../components/modal/community/InfoModal';

export default function CreatorReportTable() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const tableData = [
    {
      companyName: '현대빌딩',
      representativeName: '김철수',
      businessNumber: '000-00-00000',
      revenue: '5,500,000원',
      businessType: '법인',
      reportStatus: '생성완료',
    },
    {
      companyName: '타인의취향',
      representativeName: '한정덕',
      businessNumber: '111-11-11111',
      revenue: '3,300,000원',
      businessType: '법인',
      reportStatus: '생성완료',
    },
    {
      companyName: '대한물산',
      representativeName: '박민수',
      businessNumber: '222-22-22222',
      revenue: '2,200,000원',
      businessType: '개인',
      reportStatus: '생성불가',
    },
    {
      companyName: 'LG전자',
      representativeName: '최지영',
      businessNumber: '333-33-33333',
      revenue: '1,100,000원',
      businessType: '법인',
      reportStatus: '생성완료',
    },
    {
      companyName: '롯데마트',
      representativeName: '정수민',
      businessNumber: '444-44-44444',
      revenue: '8,800,000원',
      businessType: '법인',
      reportStatus: '생성불가',
    },
    {
      companyName: '한국기업',
      representativeName: '강동원',
      businessNumber: '555-55-55555',
      revenue: '1,500,000원',
      businessType: '개인',
      reportStatus: '생성완료',
    },
  ];

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showManualDataModal, setShowManualDataModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [clickedReports, setClickedReports] = useState(new Set());
  const [shareholderData, setShareholderData] = useState([
    { role: '대표이사', name: '', percentage: '' },
    { role: '주주1', name: '', percentage: '' },
    { role: '주주2', name: '', percentage: '' },
    { role: '주주3', name: '', percentage: '' },
    { role: '주주4', name: '', percentage: '' },
    { role: '주주5', name: '', percentage: '' },
  ]);

  const [companyData, setCompanyData] = useState([
    { label: '주당액면가', value: '' },
    { label: '대표연령', value: '' },
    { label: '주식 수', value: '' },
    { label: '매출상승률', value: '' },
  ]);

  // Filter table data based on search query
  const filteredTableData = useMemo(() => {
    if (!searchQuery.trim()) {
      return tableData;
    }

    const query = searchQuery.toLowerCase().trim();
    return tableData.filter(row => {
      const companyName = row.companyName.toLowerCase();
      const representativeName = row.representativeName.toLowerCase();
      const businessNumber = row.businessNumber.toLowerCase();

      // Check if search query matches company name + representative name (like "타인의취향 한정덕")
      const combinedName = `${companyName} ${representativeName}`;

      return (
        companyName.includes(query) ||
        representativeName.includes(query) ||
        businessNumber.includes(query) ||
        combinedName.includes(query)
      );
    });
  }, [searchQuery, tableData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleManualDataModal = () => {
    setShowManualDataModal(!showManualDataModal);
  };

  const handleSearchChange = query => {
    setSearchQuery(query);
  };

  const handleReportGeneration = companyName => {
    setClickedReports(prev => new Set([...prev, companyName]));
    setShowSuccessModal(true);
  };

  const handleShareholderChange = (index, field, value) => {
    const newData = [...shareholderData];
    newData[index][field] = value;
    setShareholderData(newData);
  };

  const handleCompanyDataChange = (index, value) => {
    const newData = [...companyData];
    newData[index].value = value;
    setCompanyData(newData);
  };

  const getTabTitle = () => {
    return tabValue === 0 ? '기업보고서' : '지원금 즐겨찾기';
  };

  return (
    <>
      <ManualDataInputModal
        showModal={showManualDataModal}
        setShowModal={setShowManualDataModal}
        shareholderData={shareholderData}
        companyData={companyData}
        handleShareholderChange={handleShareholderChange}
        handleCompanyDataChange={handleCompanyDataChange}
      />

      <SuccessModal2
        showModal={showSuccessModal}
        setShowModal={setShowSuccessModal}
        title="대기순번: 1 / 예상 대기시간: 10초"
        subtitle="요청이 대기열에 있습니다."
      />

      <InfoModal
        showModal={showInfoModal}
        setShowModal={setShowInfoModal}
        content={
          <Box sx={{ fontSize: 15, lineHeight: 1.8, color: '#222', p: 1 }}>
            <Box sx={{ mb: 1.5 }}>
              <b>
                1. 회사명 입력 후 한 칸 띄우고 대표자명을 입력하여 검색
                해주세요.
              </b>
              <br />
              <span style={{ color: '#888', fontSize: 13 }}>
                * <b>주식회사</b>, <b>(주)</b>는 제외합니다.
              </span>
              <br />
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  background: '#f5f7fa',
                  borderRadius: 1,
                  fontSize: 14,
                }}
              >
                <b>예시:</b>{' '}
                <span style={{ color: '#3f51b5' }}>삼성전자 이재용</span>
              </Box>
            </Box>
            <Box>
              <b>2. 사업자등록번호 10자리를 입력해주세요.</b>
              <br />
              <span style={{ color: '#888', fontSize: 13 }}>
                * 하이픈 (<b>-</b>)까지 입력
              </span>
              <br />
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  background: '#f5f7fa',
                  borderRadius: 1,
                  fontSize: 14,
                }}
              >
                <b>예시:</b>{' '}
                <span style={{ color: '#3f51b5' }}>000-00-00000</span>
              </Box>
            </Box>
          </Box>
        }
      />

      <Grid item container xs={12} height={isTablet ? 'auto' : '100%'}>
        <Box sx={{ width: '100%', height: '100%', px: 2, pt: 0 }}>
          {/* Tabs Section */}
          {!isTablet && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#666',
                    minWidth: 'auto',
                    padding: '12px 0',
                    marginRight: '32px',
                    '&.Mui-selected': {
                      color: theme.palette.primary.darkBlue,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.darkBlue,
                    height: '3px',
                  },
                }}
              >
                <Tab label="기업보고서" />
                <Tab label="지원금 즐겨찾기" />
              </Tabs>
            </Box>
          )}

          <FlexRowAlign
            mb={2}
            sx={{
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              flexDirection: 'row',
            }}
          >
            <FlexBox gap={0.6} style={{ cursor: 'pointer' }}>
              <FlexBox
                gap={2}
                sx={{
                  justifyContent: 'flex-start',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <H5>{getTabTitle()}</H5>

                <FlexRowAlign gap={0.5}>
                  <CircleHelp
                    size={16}
                    color={theme.palette.primary.darkBlue}
                  />
                  <Small
                    sx={{ color: theme.palette.primary.darkBlue }}
                    onClick={() => setShowInfoModal(true)}
                  >
                    검색 방법
                  </Small>
                </FlexRowAlign>
              </FlexBox>
            </FlexBox>

            <Button
              onClick={toggleManualDataModal}
              sx={{
                width: '140px',
                height: '40px',
                borderRadius: '12px',
                background: theme.palette.primary.darkBlue,
                color: theme.palette.primary.white,
                gap: 0.5,
                '&:hover': {
                  background: theme.palette.primary.darkBlueHover,
                },
              }}
            >
              <Plus size={20} color={theme.palette.primary.white} />
              주주명부 입력
            </Button>
          </FlexRowAlign>

          <FlexRowAlign mb={3} sx={{ width: '100%' }}>
            <FilterableSearchBar
              maxWidth="100%"
              onSearchChange={handleSearchChange}
              placeholder="회사명 대표자명 또는 사업자등록번호를 입력하세요"
            />
          </FlexRowAlign>

          <Box mb={2} display={'flex'} justifyContent={'flex-end'}>
            <Tiny>전체 {filteredTableData.length}건</Tiny>
          </Box>

          <Grid container sx={{ height: '100%' }}>
            <Grid item xs={12} display={'flex'} flexDirection={'column'}>
              {/* Table */}
              <Box sx={{ textAlign: 'left', mb: 2, overflow: 'auto' }}>
                <Table
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.primary.borderColor}`,
                      whiteSpace: 'nowrap',
                    },
                    minWidth: '1300px',
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: theme.palette.primary.grey800 }}
                    >
                      <TableCell
                        sx={{
                          paddingLeft: '24px !important',
                          fontWeight: 600,
                        }}
                      >
                        기업이름
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>대표자이름</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>사업자번호</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>매출액</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>사업자형태</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        보고서생성 결과
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>보고서생성</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTableData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          paddingLeft: '10px',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.grey800,
                            cursor: 'pointer',
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            paddingLeft: '24px !important',
                            fontWeight: 400,
                          }}
                        >
                          {row.companyName}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          <FlexBox alignItems="center" gap={1}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: '14px',
                                backgroundColor: theme.palette.primary.main,
                              }}
                            >
                              {row.representativeName.charAt(0)}
                            </Avatar>
                            {row.representativeName}
                          </FlexBox>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {row.businessNumber}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {row.revenue}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {row.businessType}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {clickedReports.has(row.companyName) ? (
                            row.reportStatus === '생성완료' ? (
                              <Button
                                variant="contained"
                                sx={{
                                  backgroundColor:
                                    theme.palette.primary.lightBlue3,
                                  color: theme.palette.primary.darkBlue,
                                  border: `1px solid ${theme.palette.primary.borderColor}`,
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  padding: '6px 16px',
                                  borderRadius: '8px',
                                  minWidth: '90px',
                                  height: '32px',
                                  gap: 0.5,
                                  '&:hover': {
                                    backgroundColor:
                                      theme.palette.primary.lightBlue3,
                                    opacity: 0.8,
                                  },
                                }}
                                onClick={() => {
                                  // Handle download logic here
                                  console.log(
                                    'Download report for:',
                                    row.companyName
                                  );
                                }}
                              >
                                <ArrowDownToLine
                                  size={16}
                                  color={theme.palette.primary.darkBlue}
                                />
                                다운로드
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  backgroundColor: '#FFEBEE',
                                  color: '#D32F2F',
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  width: '90px',
                                  fontWeight: 500,
                                  display: 'flex',
                                  textAlign: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Check size={16} color="#D32F2F" />
                                생성불가
                              </Box>
                            )
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: theme.palette.primary.darkBlue,
                              color: theme.palette.primary.white,
                              fontSize: '13px',
                              fontWeight: 500,
                              padding: '6px 16px',
                              borderRadius: '8px',
                              minWidth: '90px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.primary.darkBlueHover,
                              },
                            }}
                            onClick={() =>
                              handleReportGeneration(row.companyName)
                            }
                          >
                            보고서 생성
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ReversePagination totalItems={3} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </>
  );
}
